import React, { useRef, useState, useCallback, useMemo } from 'react';
import { Editor } from 'ketcher-react';
import { StandaloneStructServiceProvider } from 'ketcher-standalone';
import 'ketcher-react/dist/index.css';

export default function ChemStructure() {
  // Create once per mount — avoids WASM re-init on re-renders
  const structServiceProvider = useMemo(() => new StandaloneStructServiceProvider(), []);

  const ketcherRef    = useRef(null);
  const editorReady   = useRef(false);  // true after settling delay post-onInit
  const pendingSmiles = useRef(null);   // queued while editor warms up
  const [nameQuery, setNameQuery] = useState('');
  const [smilesInput, setSmilesInput] = useState('');
  const [searching, setSearching] = useState(false);
  const [editorLoading, setEditorLoading] = useState(true);
  const [error, setError] = useState('');
  const [compoundInfo, setCompoundInfo] = useState(null);
  const [toast, setToast] = useState('');

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(''), 2500);
  };

  // Strip SDF data fields — keep only the V2000/V3000 molfile block.
  // Ketcher 3.x hangs on the SD property section after "M  END".
  const extractMolfile = (sdf) => {
    const end = sdf.indexOf('M  END');
    return end !== -1 ? sdf.slice(0, end + 6) : sdf;
  };

  // setMolecule with a 5 s timeout so a bad input can never freeze the UI
  const safSetMolecule = (mol) =>
    Promise.race([
      ketcherRef.current.setMolecule(mol),
      new Promise((_, rej) => setTimeout(() => rej(new Error('timeout')), 5000)),
    ]);

  // Draw a molecule string (molfile or SMILES) — queues if editor not ready yet
  const drawMolecule = useCallback(async (molString) => {
    if (!molString) return;
    if (!editorReady.current || !ketcherRef.current) {
      pendingSmiles.current = molString;
      return;
    }
    try {
      await safSetMolecule(molString);
      setError('');
    } catch (e) {
      setError('Structure could not be rendered. Try pasting the SMILES manually below.');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleEditorInit = useCallback((ketcher) => {
    ketcherRef.current = ketcher;
    // In Ketcher 3.x onInit fires when the React wrapper mounts, but the WASM
    // struct service needs a moment to finish loading. A flat 1 s delay is the
    // most reliable approach — polling setMolecule('') throws even when ready.
    setTimeout(async () => {
      editorReady.current = true;
      setEditorLoading(false);
      if (pendingSmiles.current) {
        const mol = pendingSmiles.current;
        pendingSmiles.current = null;
        await safSetMolecule(mol).catch(() => {});
      }
    }, 1000);
  }, []);

  const searchByName = async () => {
    if (!nameQuery.trim()) return;
    setSearching(true);
    setError('');
    setCompoundInfo(null);
    const name = encodeURIComponent(nameQuery.trim());
    const base = 'https://pubchem.ncbi.nlm.nih.gov/rest/pug/compound/name';
    try {
      // Fetch properties + SDF in parallel
      // SDF has pre-computed 2D atom coordinates — Ketcher renders it instantly
      // without needing to run its own layout engine (which requires WASM warmup)
      const [propRes, sdfRes] = await Promise.all([
        fetch(`${base}/${name}/property/IsomericSMILES,MolecularFormula,MolecularWeight,IUPACName/JSON`),
        fetch(`${base}/${name}/SDF`),
      ]);

      if (!propRes.ok) throw new Error('not found');
      const propData = await propRes.json();
      const p = propData.PropertyTable.Properties[0];

      setCompoundInfo({
        formula: p.MolecularFormula,
        mw:      p.MolecularWeight,
        iupac:   p.IUPACName,
        smiles:  p.IsomericSMILES,
      });

      // Use SDF (molfile) if available — has 2D coords so no layout needed
      // Fall back to SMILES if SDF fetch failed
      if (sdfRes.ok) {
        const sdf = await sdfRes.text();
        await drawMolecule(extractMolfile(sdf));
      } else {
        await drawMolecule(p.IsomericSMILES);
      }
    } catch (e) {
      setError('Compound not found in PubChem. Try a different name or draw manually.');
    } finally {
      setSearching(false);
    }
  };

  const loadSmiles = async () => {
    if (!smilesInput.trim()) return;
    if (!editorReady.current || !ketcherRef.current) {
      setError('Editor is still loading. Please wait a moment and try again.');
      return;
    }
    try {
      await ketcherRef.current.setMolecule(smilesInput.trim());
      setError('');
      showToast('Structure loaded');
    } catch {
      setError('Invalid SMILES string. Please check and try again.');
    }
  };

  const copySmiles = async () => {
    if (!ketcherRef.current) return;
    try {
      const smiles = await ketcherRef.current.getSmiles();
      await navigator.clipboard.writeText(smiles);
      showToast('SMILES copied to clipboard');
    } catch { setError('Failed to get SMILES from editor.'); }
  };

  const copyMol = async () => {
    if (!ketcherRef.current) return;
    try {
      const mol = await ketcherRef.current.getMolfile();
      await navigator.clipboard.writeText(mol);
      showToast('MOL copied to clipboard');
    } catch { setError('Failed to get MOL from editor.'); }
  };

  const downloadMol = async () => {
    if (!ketcherRef.current) return;
    try {
      const mol = await ketcherRef.current.getMolfile();
      const blob = new Blob([mol], { type: 'chemical/x-mdl-molfile' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${nameQuery.trim() || 'structure'}.mol`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { setError('Failed to download MOL file.'); }
  };

  const clearEditor = async () => {
    pendingSmiles.current = null;
    if (editorReady.current && ketcherRef.current) await ketcherRef.current.setMolecule('').catch(() => {});
    setCompoundInfo(null);
    setNameQuery('');
    setSmilesInput('');
    setError('');
    showToast('Editor cleared');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', background: '#f9fafb' }}>

      {/* Header */}
      <div style={{ padding: '20px 24px 0', flexShrink: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', margin: 0 }}>Chemical Structure Editor</h1>
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: 4, marginBottom: 16 }}>
          Search by compound name for auto-draw, paste a SMILES string, or draw manually
        </p>
      </div>

      {/* Controls */}
      <div style={{ padding: '0 24px 12px', flexShrink: 0 }}>
        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>

          {/* Name search row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }}>
                <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
                </svg>
              </span>
              <input
                value={nameQuery}
                onChange={e => setNameQuery(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && searchByName()}
                placeholder="Search compound name (e.g. Aspirin, Caffeine, Paracetamol, Ibuprofen)..."
                style={{ width: '100%', padding: '8px 12px 8px 34px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
            <button
              onClick={searchByName}
              disabled={searching || editorLoading}
              title={editorLoading ? 'Editor is loading…' : ''}
              style={{ padding: '8px 20px', background: '#2563eb', color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: (searching || editorLoading) ? 'not-allowed' : 'pointer', opacity: (searching || editorLoading) ? 0.7 : 1, whiteSpace: 'nowrap' }}
            >
              {editorLoading ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  Loading editor…
                </span>
              ) : searching ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg style={{ animation: 'spin 1s linear infinite' }} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 12a9 9 0 11-6.219-8.56"/>
                  </svg>
                  Searching...
                </span>
              ) : 'Auto Draw'}
            </button>
          </div>

          {/* SMILES row */}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <input
              value={smilesInput}
              onChange={e => setSmilesInput(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadSmiles()}
              placeholder="Or paste SMILES (e.g. CC(=O)Oc1ccccc1C(=O)O)"
              style={{ flex: 1, minWidth: 200, padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, fontFamily: 'monospace', outline: 'none' }}
            />
            <button onClick={loadSmiles} style={btnStyle('#059669', '#fff')}>Load SMILES</button>
            <button onClick={copySmiles} style={btnStyle('#f3f4f6', '#374151', true)}>Copy SMILES</button>
            <button onClick={copyMol}    style={btnStyle('#f3f4f6', '#374151', true)}>Copy MOL</button>
            <button onClick={downloadMol} style={btnStyle('#f3f4f6', '#374151', true)}>
              <svg width="14" height="14" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2" style={{ marginRight: 4, verticalAlign: 'middle' }}>
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
              </svg>
              Download .mol
            </button>
            <button onClick={clearEditor} style={btnStyle('#fef2f2', '#dc2626', true, '#fca5a5')}>Clear</button>
          </div>

          {/* Compound info */}
          {compoundInfo && (
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', padding: '8px 12px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, fontSize: 13 }}>
              <InfoBadge label="Formula" value={compoundInfo.formula} />
              <InfoBadge label="MW" value={`${compoundInfo.mw} g/mol`} />
              {compoundInfo.iupac && <InfoBadge label="IUPAC" value={compoundInfo.iupac} />}
              {compoundInfo.smiles && (
                <span style={{ color: '#166534', fontFamily: 'monospace', fontSize: 12 }}>
                  <b>SMILES:</b> {compoundInfo.smiles.length > 90 ? compoundInfo.smiles.slice(0, 90) + '…' : compoundInfo.smiles}
                </span>
              )}
            </div>
          )}

          {/* Error */}
          {error && (
            <div style={{ color: '#dc2626', fontSize: 13, padding: '7px 12px', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 6 }}>
              {error}
            </div>
          )}
        </div>
      </div>

      {/* Ketcher Editor */}
      <div style={{ flex: 1, minHeight: 0, margin: '0 24px 24px', border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
        <Editor
          staticResourcesUrl={process.env.PUBLIC_URL}
          structServiceProvider={structServiceProvider}
          onInit={handleEditorInit}
          errorHandler={err => console.error('Ketcher error:', err)}
        />
      </div>

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', bottom: 24, right: 24, background: '#1f2937', color: '#fff', padding: '10px 18px', borderRadius: 8, fontSize: 14, zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.25)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="#4ade80" strokeWidth="2.5">
            <polyline points="20 6 9 17 4 12"/>
          </svg>
          {toast}
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

const btnStyle = (bg, color, bordered = false, borderColor = '#d1d5db') => ({
  padding: '8px 14px',
  background: bg,
  color,
  border: bordered ? `1px solid ${borderColor}` : 'none',
  borderRadius: 6,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  whiteSpace: 'nowrap',
  display: 'inline-flex',
  alignItems: 'center',
});

const InfoBadge = ({ label, value }) => (
  <span style={{ color: '#166534' }}>
    <b>{label}:</b> {value}
  </span>
);
